import { useCallback } from 'react';
import { Walkthrough, type WalkthroughStep } from '@/components/ui/walkthrough';

const STEPS: WalkthroughStep[] = [
  {
    target: 'details',
    title: 'Strategy Details',
    description:
      'Give your strategy a name, pick the symbol (market) to trade, and add an optional description. The symbol is locked after creation.',
  },
  {
    target: 'timeframe',
    title: 'Timeframe',
    description:
      "Choose how often candles update. Shorter = more signals but more noise. 5m is good for scalping, 1h for swing trading.",
  },
  {
    target: 'entry-rules',
    title: 'Entry Rules',
    description:
      "Define when to open trades. Set a direction (BUY, SELL, or SIGNAL for both), then add conditions like 'RSI < 30'. You can combine multiple conditions with AND/OR logic.",
  },
  {
    target: 'set-condition',
    title: 'Adding Conditions',
    description:
      "Click 'Set Condition' to pick from 50+ presets (RSI, MACD, Bollinger Bands, etc.) or build your own. Combine conditions with AND (all must be true) or OR (any can be true).",
  },
  {
    target: 'lot-sizing',
    title: 'Position Sizing',
    description:
      'Control how much to trade. Fixed = same lot every time. Risk-Based = % of equity risked per trade. Equity % = scales with your account size.',
  },
  {
    target: 'grid-section',
    title: 'Grid Configuration',
    description:
      'Optional grid trading mode. Opens additional positions at set intervals as price moves against you, averaging down the entry price. Set step size and max levels.',
  },
  {
    target: 'exit-rules',
    title: 'Exit Rules',
    description:
      "Define when to close trades. Set conditions like 'floating P&L > $5' with actions like Close All or Close Partial (scale out). Add multiple exit rules for layered exits.",
  },
  {
    target: 'trailing-section',
    title: 'Trailing Stop',
    description:
      'Locks in profits as price moves in your favor. Choose ATR-based, percentage, or fixed distance. The stop follows price up and triggers when it reverses.',
  },
  {
    target: 'risk-section',
    title: 'Risk Management',
    description:
      'Set a safety net. Equity SL stops the bot if your account drops too far. Max Positions caps how many trades can be open at once.',
  },
];

// Map from step target → the accordion section key that must be open
const SECTION_FOR_TARGET: Record<string, string> = {
  timeframe: 'market',
  'entry-rules': 'entry',
  'set-condition': 'entry',
  'lot-sizing': 'entry',
  'grid-section': 'grid',
  'exit-rules': 'exit',
  'trailing-section': 'trailing',
  'risk-section': 'risk',
};

interface EditorWalkthroughProps {
  active: boolean;
  onComplete: () => void;
  onExpandSection: (sectionKey: string) => void;
}

export function EditorWalkthrough({ active, onComplete, onExpandSection }: EditorWalkthroughProps) {
  const handleBeforeStep = useCallback(
    (_stepIndex: number, target: string) => {
      const section = SECTION_FOR_TARGET[target];
      if (section) {
        onExpandSection(section);
      }
    },
    [onExpandSection],
  );

  return (
    <Walkthrough
      steps={STEPS}
      active={active}
      onComplete={onComplete}
      onBeforeStep={handleBeforeStep}
    />
  );
}
